import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { VesselService } from '@service/vessel.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType, getAllTypesSet, getAnalogTypesSet } from 'icreate/common/models/channel-type';
import { ExhaustSetting } from 'icreate/common/models/exhaust-gas-setting';
import { fromEvent, lastValueFrom, Subject, take, takeUntil } from 'rxjs';

enum Selector {
    UNDEFINED = 'undefined',
    TEMP = 'temp',
    DEVIATION = 'deviation',
}

@Component({
    selector: 'app-exhaust-setting-row',
    templateUrl: './exhaust-setting-row.component.html',
    styleUrl: './exhaust-setting-row.component.css',
    standalone: false,
})
export class ExhaustSettingRowComponent implements OnInit, OnDestroy {
    readonly TEMP = Selector.TEMP;
    readonly DEVIATION = Selector.DEVIATION;

    @Input() setting: ExhaustSetting;
    @Input() vesselId: string;
    @Input() settingChannels: any;
    @Input() isAddSettingDisabled: boolean;

    @Output() onDuplicateRow: EventEmitter<ExhaustSetting> = new EventEmitter();
    @Output() onDeleteRow: EventEmitter<ExhaustSetting> = new EventEmitter();
    @Output() onRowUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('settingRow') settingRow: ElementRef;
    @ViewChild('removeConfirmation') removeConfirmation: ConfirmationWindowComponent;
    @ViewChild('otherOptions') otherOptions: ElementRef;
    @ViewChild('optionsIcon') optionsIcon: ElementRef;

    allowedTypesSet: Set<ChannelType> = getAllTypesSet();
    isTempMessageVisible: boolean = false;
    isDevMessageVisible: boolean = false;
    optionsSettingOpen: boolean = false;

    infoMessageDistance: string;
    editRowTop: string;
    editRowRight: string;

    readonly canEditVessel = toSignal(this.vesselService.accessUpdated$, { initialValue: false });
    private destroy$: Subject<void> = new Subject();

    constructor(
        private renderer: Renderer2,
        private vesselService: VesselService,
        private dialog: MatDialog
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.optionsIcon?.nativeElement.contains(event.target) && this.optionsSettingOpen) {
            this.optionsSettingOpen = false;
        }
    }

    @HostListener('window:resize')
    onWindowChange() {
        if (this.optionsSettingOpen) {
            this.optionsSettingOpen = false;
        }
    }

    ngOnInit(): void {
        const scrollContainer = document.querySelector('.channel-wrap');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.optionsSettingOpen = false;
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getChannelName(selector: Selector) {
        const isTemp = selector === Selector.TEMP;
        const isDeviation = selector === Selector.DEVIATION;

        if (isTemp && this.setting.tempChannelData) {
            return this.setting.tempChannelData.opsData.name;
        } else if (isDeviation && this.setting.deviationChannelData) {
            return this.setting.deviationChannelData.opsData.name;
        }
    }

    async selectChannel(selector: Selector) {
        if (!this.canEditVessel()) return;
        const isTemp = selector === Selector.TEMP;
        const isDeviation = selector === Selector.DEVIATION;

        let assignedChannel: Channel | null = null;
        let allowedTypesSet = new Set<ChannelType>();
        if (isTemp) {
            assignedChannel = this.setting.tempChannelData || null;
            allowedTypesSet = new Set(getAnalogTypesSet());
        } else if (isDeviation) {
            assignedChannel = this.setting.deviationChannelData || null;
            allowedTypesSet = new Set([ChannelType.EXH_GAS_DEV]);
        }

        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vesselId,
                assignedChannel: assignedChannel,
                allowedTypesSet: allowedTypesSet,
                setShouldDisableTypes: true,
            },
        });

        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());

        if (!channelSelected) return;
        if (isTemp) {
            this.setting.tempChannel = channelSelected.id;
            this.setting.tempChannelData = channelSelected;
        } else if (isDeviation) {
            this.setting.deviationChannel = channelSelected.id;
            this.setting.deviationChannelData = channelSelected;
        }
        this.rowUpdated(true);
    }

    async removeChannel(selector: Selector) {
        const isTemp = selector === Selector.TEMP;
        const isDeviation = selector === Selector.DEVIATION;

        this.removeConfirmation.setVisible(true);
        let confirmationOption = await lastValueFrom(this.removeConfirmation.onOptionSelected.pipe(take(1)));
        if (confirmationOption !== ConfirmationWindowTypes.SAVE) return;

        if (isTemp) {
            this.setting.tempChannel = null;
            this.setting.tempChannelData = null;
        } else if (isDeviation) {
            this.setting.deviationChannel = null;
            this.setting.deviationChannelData = null;
        }
        this.rowUpdated(true);
    }

    updateInfoMessage(selector: Selector) {
        const isTemp = selector === Selector.TEMP;
        const isDeviation = selector === Selector.DEVIATION;
        this.updateTopPosition();

        if (isTemp) {
            this.isTempMessageVisible = true;
        } else if (isDeviation) {
            this.isDevMessageVisible = true;
        }
    }

    openOptions() {
        if (this.optionsSettingOpen) {
            this.optionsSettingOpen = false;
            return;
        }
        this.updateTopPosition();
        this.optionsSettingOpen = true;
    }

    updateTopPosition() {
        if (!this.settingRow?.nativeElement || !this.settingChannels) return;

        const distanceFromTop = this.settingRow.nativeElement.getBoundingClientRect().top;

        this.infoMessageDistance = distanceFromTop + 42 + 'px';

        if (this.optionsIcon && this.optionsIcon.nativeElement) {
            const rect = this.optionsIcon.nativeElement.getBoundingClientRect();
            this.editRowTop = distanceFromTop + 40 + 'px';
            this.editRowRight = window.innerWidth - rect.right + 20 + 'px';
        }
    }

    duplicateRow() {
        if (!this.canEditVessel()) return;
        this.onDuplicateRow.emit(this.setting);
    }

    deleteRow() {
        if (!this.canEditVessel()) return;
        this.onDeleteRow.emit(this.setting);
    }

    rowUpdated(value: boolean) {
        this.onRowUpdated.emit(value);
    }
}
